import axios, { ResponseType } from 'axios';
import { nowUnix, formatUnix } from './SiteUtil';
import { APIResponse, UserClass, PromoterStatus, Asset, EventItem, ExternalEvent, ExternalUploadResult, PromoterActivity, PromoterCondition, PromoterListItem, ProspectListItem, SurveyFormQuestion, SurveyResponseItem } from './SiteClasses';

export const CDN_URL = process.env.REACT_APP_CDN_URL;
export const PAGE_URL = process.env.REACT_APP_PAGE_URL;
const PAGE_AUTH = process.env.REACT_APP_AUTH_URL;
const ROOT_URL = process.env.REACT_APP_API_URL;
const DEBUG = process.env.REACT_APP_DEBUG=='1';
const LOGIN_SOURCE = 3;

export interface TokenData{
	access: string,
	expires: number,
}

export interface UserLogin{
	user: {
		admin_id: number,
		email: string,
		first_name: string,
		last_name: string,
		superadmin: boolean,
	},
	access: number[]
}

interface PostOptions{
	login?: boolean,
	response_type?: ResponseType,
	access_token?: string,
}

var CACHE : { [x: string]: APIResponse<any> } = {};
var USER_LOGIN_CACHE : UserLogin = null;

var getToken = () : TokenData=>{
	var jsonstr = localStorage.getItem('access_token');
	if(!jsonstr) return null;
	try{
		var data = JSON.parse(jsonstr) as TokenData;
		if(!data) return null;
		if(!data.access) return null;
		var now = nowUnix();
		if(data.expires<=(now+30)) return null;
		return data;
	}catch(e){
		return null;
	}
}

var saveToken = (token_data: TokenData)=>{
	localStorage.setItem('access_token', JSON.stringify(token_data));
	window.dispatchEvent(new Event('user_token'));
}

var logout = ()=>{
	localStorage.removeItem('access_token');
	localStorage.removeItem('user_data');
	window.dispatchEvent(new Event('user_data'));
	window.dispatchEvent(new Event('user_logout'));
}

async function post<T=any>(endpoint: string, data?: any, opts: PostOptions={}) : Promise<APIResponse<T>>{
	var options = {
		headers: {
			'show-code': DEBUG ? 1 : 0,
			'token': '',
			'Content-Type': 'application/json'
		}
	}
	if(opts?.login!==false){
		if(opts?.access_token){
			options.headers.token = opts.access_token;
		}else{
			var u = getToken();
			if(u?.access){
				options.headers.token = u.access;
			}
		}
	}
	if(data instanceof FormData){
		options.headers['Content-Type'] = 'multipart/form-data';
	}

	try{
		var ENDPOINT = `${ROOT_URL}/${endpoint}`;
		if(DEBUG) console.log("POST "+ENDPOINT);
		var res = await axios.post(ENDPOINT, data, {
			...options,
			responseType: opts?.response_type || 'json'
		});
		if(DEBUG) console.log(` => Response: ${(Number(res.headers['content-length'])/1024).toFixed(2)} KB`);
		if(res.data && res.data.error && (res.data.code=='P-TKNE' || res.data.code=='P-TKNV')){
			logout();
		}
		return res.data as APIResponse<T>;
	}catch(err){
		if(DEBUG) console.error(err);
		if((err as any)?.code==="ERR_NETWORK"){
			return {
				error: true,
				message: 'No se pudo establecer una conexión con el servidor. (API-NONTWK)',
				code: 'API-NONTWK',
			}
		}
		return {
			error: true,
			message: 'Hubo un error inesperado (API-LCL-1)',
			code: 'API-LCL-1',
		}
	}
};

var cachePost = async <T=any>(endpoint: string, key: string, data?: any) : Promise<APIResponse<T>>=>{
	if(CACHE[key]) return CACHE[key] as APIResponse<T>;
	var res = await post(endpoint, data);
	if(!res.error) CACHE[key] = res;
	return res;
}

var getAuthUrl = ()=>{
	if(!PAGE_AUTH) return null;
	return `${PAGE_AUTH}/admin?s=${LOGIN_SOURCE}&continue=${encodeURIComponent(`${window.location.origin}/auth`)}&redirect=${encodeURIComponent(window.location.href)}`
}

var onLoginToken = async (token: string, expiration: number, dispatch_event: boolean=true)=>{
	var res = await post<{ token: { access: string, expires: number } }>('auth/login', { token });
	if(!res.error){
		saveToken(res.data.token);
		if(dispatch_event) window.dispatchEvent(new Event('user_token'));
		getLogin(true, res.data.token.access, true);
	}
	return res;
}

var getLogin = async (force: boolean=false, token: string=null, dispatch_event: boolean=true)=>{
	if(!force && USER_LOGIN_CACHE) return { error: false, data: USER_LOGIN_CACHE, message: '' }

	var access_token = token;
	if(!access_token){
		var tkn = getToken();
		if(!tkn) return { error: true, message: 'El usuario no tiene un token de autorización', code: 'NOTKN' };
		access_token = tkn.access;
	}

	var res = await post<UserLogin>('users/me', {}, {
		access_token: access_token
	});
	if(res.error) return res;
	USER_LOGIN_CACHE = res.data;
	localStorage.setItem('user_data', JSON.stringify(res.data));
	setTimeout(()=>{
		if(dispatch_event) window.dispatchEvent(new Event('user_data'));
	}, 50);
	return res;
}

var getMetadatasList = (count: number, offset: number)=>{
	return post('metadata/list', { count, offset });
}

var getMetadata = (metadata_id: number)=>{
	return post('metadata/get', { metadata_id });
}

var createMetadata = (type_id: number, metadata_name: string, metadata_comment: string)=>{
	return post('metadata/create', { type_id, metadata_name, metadata_comment });
}

var editMetadata = (metadata_id: number, type_id: number, metadata_name: string, metadata_comment: string)=>{
	return post('metadata/edit', { metadata_id, type_id, metadata_name, metadata_comment });
}

var deleteMetadata = (metadata_id: number, deleted: boolean)=>{
	return post('metadata/delete', { metadata_id, deleted });
}

var saveMetadataSort = (order: number[])=>{
	return post('metadata/sort/update', { order });
}

var searchMetadata = (query: string)=>{
	return post('metadata/search', { query });
}

var createMetadataValue = (metadata_id: number, value: string)=>{
	return post('metadata/value/create', { metadata_id, value });
}

var editMetadataValue = (value_id: number, value: string)=>{
	return post('metadata/value/edit', { value_id, value });
}

var deleteMetadataValue = (value_id: number)=>{
	return post('metadata/value/delete', { value_id });
}

var getActivityTypes = (active: boolean=false)=>{
	return post('activity-types/list', { active });
}

var createActivityType = (type_name: string)=>{
	return post('activity-types/create', { type_name });
}

var editActivityType = (activity_type_id: number, type_name: string)=>{
	return post('activity-types/edit', { activity_type_id, type_name });
}

var deleteActivityType = (activity_type_id: number)=>{
	return post('activity-types/delete', { activity_type_id });
}

var restoreActivityType = (activity_type_id: number)=>{
	return post('activity-types/restore', { activity_type_id });
}

interface ListFilters{ search?: string, user_id?: number, status_id?: number, page?: number }

var getPromoters = (filters?: ListFilters)=>{
	return post<{ promoters: PromoterListItem[], total: number }>('promoters/list', filters || {});
}

var getInactivePromoters = (filters?: ListFilters)=>{
	return post<{ promoters: PromoterListItem[], total: number }>('promoters/inactivos/list', filters || {});
}

var getConvertirProspects = (filters?: ListFilters)=>{
	return post<{ prospects: ProspectListItem[], total: number }>('promoters/convertir/list', filters || {});
}

var getAplazadoProspects = (filters?: ListFilters)=>{
	return post<{ prospects: ProspectListItem[], total: number }>('promoters/aplazados/prospects/list', filters || {});
}

var getDescalificadoProspects = (filters?: ListFilters)=>{
	return post<{ prospects: ProspectListItem[], total: number }>('promoters/descalificados/prospects/list', filters || {});
}

var getProspects = (filters?: ListFilters)=>{
	return post<{ prospects: ProspectListItem[], total: number }>('promoters/prospects/list', filters || {});
}

var getProspect = (prospect_id: number)=>{
	return post('promoters/prospects/get', { prospect_id });
}

var getPromoterInfo = (promoter_id: number)=>{
	return post('promoters/get', { promoter_id });
}

interface ProspectAnswer{ metadata_id: number, value_id?: number, value?: string }

var getProspectTemplate = ()=>{
	return post('promoters/prospects/template');
}

var createProspect = (prospect_name: string, answers: ProspectAnswer[], user_id?: number | null)=>{
	return post<{ prospect_id: number }>('promoters/prospects/create', { prospect_name, answers, user_id });
}

var getPublicProspectTemplate = ()=>{
	return post('public/form/template', {}, { login: false });
}

var registerProspect = (prospect_name: string, answers: ProspectAnswer[])=>{
	return post<{ prospect_id: number }>('public/prospect/register', { prospect_name, answers }, { login: false });
}

var getStatuses = ()=>{
	return post<PromoterStatus[]>('promoters/statuses');
}

var setRecordStatus = (target: CommentTarget, status_id: number)=>{
	return post('promoters/status/set', { ...target, status_id });
}

var assignRecord = (target: CommentTarget, user_id: number | null)=>{
	return post('promoters/assign', { ...target, user_id });
}

var renameRecord = (target: CommentTarget, name: string)=>{
	return post('promoters/rename', { ...target, name });
}

var deleteRecord = (target: CommentTarget)=>{
	return post('promoters/delete', target);
}

var convertProspect = (prospect_id: number, promoter_id: number)=>{
	return post<{ promoter_id: number }>('promoters/prospects/convert', { prospect_id, promoter_id });
}

var getSidebarCounts = ()=>{
	return post<{ prospects_recent: number, convertir: number }>('promoters/sidebar/counts', {});
}

interface ReportUserCount{ user_assigned: number | null, user_name: string | null, total: number }
interface ReportStats{ prospects_created: number, prospects_converted: number, prospects_deleted: number, promoters_created: number }
var getDatesReport = (date_start: number, date_end: number)=>{
	return post<{ prospects: ReportUserCount[], promoters: ReportUserCount[], stats: ReportStats }>('reports/dates', { date_start, date_end });
}

var downloadDatesReport = async(date_start: number, date_end: number)=>{
	var res = (await post('reports/dates/csv', { date_start, date_end }, {
		response_type: 'blob'
	}) as any) as Blob;

	if(!(res instanceof Blob)) return res as APIResponse<any>;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = 'reporte_fechas.csv';
	link.click();
	link.remove();
	URL.revokeObjectURL(url);

	return { error: false } as APIResponse<any>;
}

interface SurveyReportRow{ question_id: number, question: string, type_id: number, answer_value: string, total: number }
interface SurveyReportUser{ user_assigned: number | null, user_name: string | null, responses: number, answers: number }
interface SurveyReportPromoter{ question: string, promoter_id: number, promoter_name: string, answer_value: string, total: number }
var getSurveysReport = (date_start: number, date_end: number, user_assigned?: number)=>{
	return post<{ rows: SurveyReportRow[], users: SurveyReportUser[], promoters: SurveyReportPromoter[], responses: number }>('reports/surveys', { date_start, date_end, user_assigned });
}

export interface StateEventRow{ date_id: number, event_name: string, date: number, venue_name: string | null, city: string | null, zipcode: number | null, lattitude: number | null, longitude: number | null, tickets_sold: number, promoter_id: number, promoter_name: string, user_assigned: number | null, user_name: string | null, state_id: number | null, state_name: string | null }
var getStatesReport = (date_start: number, date_end: number, promoter_id?: number, user_assigned?: number)=>{
	return post<StateEventRow[]>('reports/states', { date_start, date_end, promoter_id, user_assigned });
}

var downloadPromotersReport = async(date_start: number, date_end: number)=>{
	var res = (await post('reports/promoters/csv', { date_start, date_end }, {
		response_type: 'blob'
	}) as any) as Blob;

	if(!(res instanceof Blob)) return res as APIResponse<any>;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `reporte_promotores_${formatUnix(date_start, 'yyyy-MM-dd')}_a_${formatUnix(date_end, 'yyyy-MM-dd')}.csv`;
	link.click();
	link.remove();
	URL.revokeObjectURL(url);

	return { error: false } as APIResponse<any>;
}

var searchUnlinkedPromoters = (search?: string)=>{
	return post<{ promoter_id: number, promoter_name: string }[]>('promoters/unlinked/search', { search });
}

var syncPromoters = ()=>{
	return post<{ synced: number }>('promoters/sync', {});
}

var getExternalEvents = ()=>{
	return post<ExternalEvent[]>('external-events/list', {});
}

var uploadExternalEvents = (rows: { [column: string]: any }[], first_row: number)=>{
	return post<ExternalUploadResult>('external-events/upload', { rows, first_row });
}

var getPromoterForm = (prospect_id: number)=>{
	return post('promoters/metadata/get', { prospect_id });
}

type CommentTarget = { prospect_id?: number, promoter_id?: number, include_prospect_id?: number };

var getComments = (target: CommentTarget)=>{
	return post('promoters/comments/list', target);
}

var createComment = (target: CommentTarget, content: string, mentioned_users: number[])=>{
	return post<{ comment_id: number }>('promoters/comments/create', { ...target, content, mentioned_users });
}

var deleteComment = (comment_id: number)=>{
	return post('promoters/comments/delete', { comment_id });
}

var searchUsers = (query: string)=>{
	return post('users/search', { query });
}

var getUsers = (count: number=50, offset: number=0)=>{
	return post<UserClass[]>('users/list', { count, offset });
}

var getUser = (user_id: number)=>{
	return post<UserClass>('users/get', { user_id });
}

var userAccess = (user_id: number, access: number[])=>{
	return post('users/access', { user_id, access });
}

var setUserSuperadmin = (user_id: number, superadmin: boolean)=>{
	return post('users/superadmin', { user_id, superadmin });
}

interface ActivityData{ activity_type_id: number, title: string, description?: string, outcome?: string, date_activity: number, completed: boolean }

var getActivities = (target: CommentTarget)=>{
	return post('promoters/activities/list', target);
}

var getAllActivities = (filters: { user_id?: number, activity_type_id?: number, status?: 'completed' | 'pending' | 'overdue', page?: number })=>{
	return post<{ activities: PromoterActivity[], total: number }>('promoters/activities/all', filters);
}

var createActivity = (target: CommentTarget, data: ActivityData)=>{
	return post<{ activity_id: number }>('promoters/activities/create', { ...target, ...data });
}

var updateActivity = (activity_id: number, data: Omit<ActivityData, 'completed'>)=>{
	return post('promoters/activities/edit', { activity_id, ...data });
}

var completeActivity = (activity_id: number, completed: boolean=true)=>{
	return post('promoters/activities/complete', { activity_id, completed });
}

var deleteActivity = (activity_id: number)=>{
	return post('promoters/activities/delete', { activity_id });
}

var setPromoterFormAnswer = (prospect_id: number, metadata_id: number, value_id: number, value: string)=>{
	return post('promoters/metadata/set', { prospect_id, metadata_id, value_id, value });
}

interface ConditionData{ title: string, description?: string }

var getConditions = (promoter_id: number)=>{
	return post<PromoterCondition[]>('promoters/conditions/list', { promoter_id });
}

var createCondition = (promoter_id: number, data: ConditionData)=>{
	return post<{ note_id: number }>('promoters/conditions/create', { promoter_id, ...data });
}

var updateCondition = (note_id: number, data: ConditionData)=>{
	return post('promoters/conditions/edit', { note_id, ...data });
}

var deleteCondition = (note_id: number)=>{
	return post('promoters/conditions/delete', { note_id });
}

var getAssets = (target: CommentTarget)=>{
	return post<Asset[]>('promoters/assets/list', target);
}

var getEvents = (promoter_id: number)=>{
	return post<EventItem[]>('promoters/events/list', { promoter_id });
}

var uploadAsset = (target: CommentTarget, file: File, asset_name: string, type_id: number, description?: string)=>{
	var form = new FormData();
	if(target.prospect_id) form.append('prospect_id', String(target.prospect_id));
	if(target.promoter_id) form.append('promoter_id', String(target.promoter_id));
	form.append('asset_name', asset_name);
	form.append('type_id', String(type_id));
	if(description) form.append('description', description);
	form.append('file', file);
	return post<{ asset_id: number, url: string }>('promoters/assets/create', form);
}

var deleteAsset = (asset_id: number)=>{
	return post('promoters/assets/delete', { asset_id });
}

var getMetadataList = ()=>{
	return post('metadata/list');
}

var getMetadataValues = (metadata_id: number)=>{
	return post('metadata/values', { metadata_id });
}

var getPublicPromoterForm = (prospect_id: number)=>{
	return post('public/form/get', { prospect_id }, { login: false });
}

var setPublicPromoterFormAnswer = (prospect_id: number, metadata_id: number, value_id: number, value: string)=>{
	return post('public/form/set', { prospect_id, metadata_id, value_id, value }, { login: false });
}

var getPublicMetadataValues = (metadata_id: number)=>{
	return post('public/form/values', { metadata_id }, { login: false });
}

var getSurveyQuestions = (count?: number, offset?: number)=>{
	return post('surveys/list', { count, offset });
}

var getSurveyQuestion = (question_id: number)=>{
	return post('surveys/get', { question_id });
}

var createSurveyQuestion = (type_id: number, question: string, question_comment: string, default_value: string, required: boolean)=>{
	return post('surveys/create', { type_id, question, question_comment, default_value, required });
}

var editSurveyQuestion = (question_id: number, type_id: number, question: string, question_comment: string, default_value: string, required: boolean)=>{
	return post<{ question_id: number, replaced: boolean }>('surveys/edit', { question_id, type_id, question, question_comment, default_value, required });
}

var deleteSurveyQuestion = (question_id: number, deleted: boolean)=>{
	return post('surveys/delete', { question_id, deleted });
}

var saveSurveySort = (order: number[])=>{
	return post('surveys/sort/update', { order });
}

var createSurveyValue = (question_id: number, value: string)=>{
	return post('surveys/value/create', { question_id, value });
}

var editSurveyValue = (value_id: number, value: string)=>{
	return post('surveys/value/edit', { value_id, value });
}

var deleteSurveyValue = (value_id: number)=>{
	return post('surveys/value/delete', { value_id });
}

var createSurveyLink = (promoter_id: number)=>{
	return post<{ token: string }>('surveys/link', { promoter_id });
}

var getSurveyHistory = (promoter_id: number)=>{
	return post<SurveyResponseItem[]>('surveys/history', { promoter_id });
}

var getSurveyResponses = ()=>{
	return post<SurveyResponseItem[]>('surveys/responses', {});
}

var getSurveyResponse = (response_id: number)=>{
	return post<SurveyResponseItem>('surveys/response', { response_id });
}

var getPublicSurvey = (token: string)=>{
	return post<SurveyFormQuestion[]>('public/survey/get', { token }, { login: false });
}

var answerPublicSurvey = (token: string, answers: { question_id: number, value_id?: number, answer?: string }[])=>{
	return post<{ response_id: number }>('public/survey/answer', { token, answers }, { login: false });
}

export default {
	getAuthUrl,
	getLogin,
	onLoginToken,
	getToken,
	logout,
	post,
	cachePost,
	getMetadatasList,
	getMetadata,
	createMetadata,
	editMetadata,
	deleteMetadata,
	saveMetadataSort,
	searchMetadata,
	createMetadataValue,
	editMetadataValue,
	deleteMetadataValue,
	getActivityTypes,
	createActivityType,
	editActivityType,
	deleteActivityType,
	restoreActivityType,
	getPromoters,
	getInactivePromoters,
	getConvertirProspects,
	getAplazadoProspects,
	getDescalificadoProspects,
	getProspects,
	getProspect,
	getPromoterInfo,
	getProspectTemplate,
	createProspect,
	getPublicProspectTemplate,
	registerProspect,
	getStatuses,
	setRecordStatus,
	assignRecord,
	renameRecord,
	deleteRecord,
	convertProspect,
	searchUnlinkedPromoters,
	syncPromoters,
	getSidebarCounts,
	getExternalEvents,
	uploadExternalEvents,
	getDatesReport,
	downloadDatesReport,
	downloadPromotersReport,
	getSurveysReport,
	getStatesReport,
	getPromoterForm,
	getComments,
	createComment,
	deleteComment,
	searchUsers,
	getUsers,
	getUser,
	userAccess,
	setUserSuperadmin,
	getActivities,
	getAllActivities,
	createActivity,
	updateActivity,
	completeActivity,
	deleteActivity,
	getAssets,
	getEvents,
	uploadAsset,
	deleteAsset,
	setPromoterFormAnswer,
	getConditions,
	createCondition,
	updateCondition,
	deleteCondition,
	getMetadataList,
	getMetadataValues,
	getPublicPromoterForm,
	setPublicPromoterFormAnswer,
	getPublicMetadataValues,
	getSurveyQuestions,
	getSurveyQuestion,
	createSurveyQuestion,
	editSurveyQuestion,
	deleteSurveyQuestion,
	saveSurveySort,
	createSurveyValue,
	editSurveyValue,
	deleteSurveyValue,
	createSurveyLink,
	getSurveyHistory,
	getSurveyResponses,
	getSurveyResponse,
	getPublicSurvey,
	answerPublicSurvey,
}
